import { FormEvent, PointerEvent as ReactPointerEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppWindow, ArrowUpRight, BriefcaseBusiness, Check, ChevronLeft, ChevronRight,
  CircleUserRound, Code2, ExternalLink, FolderGit2, GraduationCap, Images, Mail, Maximize2, Menu,
  Minimize2, Music2, Pause, Play, Search, Send, SkipBack, SkipForward, TerminalSquare, Volume2, Wifi,
  X
} from 'lucide-react';
import { profile, projects, skills, timeline } from './data';

const apiUrl = (path: string) => import.meta.env.PROD ? `/api/${path}.php` : `/api/${path}`;

type AppId = 'finder' | 'about' | 'gallery' | 'skills' | 'projects' | 'experience' | 'contact' | 'music' | 'terminal';
type Position = { x: number; y: number };
type AppDefinition = { id: AppId; label: string; icon: ReactNode; color: string };

const apps: AppDefinition[] = [
  { id: 'about', label: 'About', icon: <CircleUserRound />, color: '#ff9a62' },
  { id: 'gallery', label: 'Gallery', icon: <Images />, color: '#f5d36f' },
  { id: 'skills', label: 'Skills', icon: <Code2 />, color: '#64d7d0' },
  { id: 'projects', label: 'Projects', icon: <FolderGit2 />, color: '#a9d55d' },
  { id: 'experience', label: 'Experience', icon: <BriefcaseBusiness />, color: '#b8a4ff' },
  { id: 'contact', label: 'Contact', icon: <Mail />, color: '#6ca9ff' },
  { id: 'music', label: 'Music', icon: <Music2 />, color: '#1ed760' },
  { id: 'terminal', label: 'Terminal', icon: <TerminalSquare />, color: '#272c2b' }
];

const dockAppIds: AppId[] = ['about', 'skills', 'projects', 'experience', 'contact'];
const desktopAppIds: AppId[] = ['gallery', 'music', 'terminal'];

const initialPositions: Record<AppId, Position> = {
  finder: { x: 80, y: 72 }, about: { x: 90, y: 78 }, gallery: { x: 150, y: 82 }, skills: { x: 200, y: 95 }, projects: { x: 120, y: 70 },
  experience: { x: 230, y: 80 }, contact: { x: 300, y: 100 }, music: { x: 180, y: 75 }, terminal: { x: 260, y: 115 }
};

function AppIcon({ app, onOpen, compact = false }: { app: AppDefinition; onOpen: () => void; compact?: boolean }) {
  return <button className={compact ? 'app-icon dock-icon' : 'app-icon'} onClick={onOpen} aria-label={`Open ${app.label}`}>
    <span className="icon-tile" style={{ '--app-color': app.color } as React.CSSProperties}>{app.icon}</span>
    {!compact && <span>{app.label}</span>}
    {compact && <span className="dock-tooltip" aria-hidden="true">{app.label}</span>}
  </button>;
}

function Window({ app, active, minimized, maximized, position, onFocus, onClose, onMinimize, onMaximize, onMove, children }:
  { app: AppDefinition; active: boolean; minimized: boolean; maximized: boolean; position: Position; onFocus: () => void; onClose: () => void; onMinimize: () => void; onMaximize: () => void; onMove: (position: Position) => void; children: ReactNode }) {
  const windowRef = useRef<HTMLElement>(null);
  const drag = useRef<{ pointerId: number; sx: number; sy: number; ox: number; oy: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const beginDrag = (event: ReactPointerEvent) => {
    if (maximized || window.innerWidth < 740 || (event.target as HTMLElement).closest('button')) return;
    drag.current = { pointerId: event.pointerId, sx: event.clientX, sy: event.clientY, ox: position.x, oy: position.y };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const moveDrag = (event: ReactPointerEvent) => {
    if (!drag.current || drag.current.pointerId !== event.pointerId) return;
    const bounds = windowRef.current?.getBoundingClientRect();
    const minVisibleWidth = Math.min(160, bounds?.width ?? 160);
    const maxX = window.innerWidth - minVisibleWidth;
    const maxY = window.innerHeight - 80;
    const x = Math.max(8 - (bounds?.width ?? 0) + minVisibleWidth, Math.min(maxX, drag.current.ox + event.clientX - drag.current.sx));
    const y = Math.max(34, Math.min(maxY, drag.current.oy + event.clientY - drag.current.sy));
    onMove({ x, y });
  };
  const endDrag = (event: ReactPointerEvent) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
  };
  if (minimized) return null;
  return <section
    ref={windowRef}
    className={`window window-${app.id} ${active ? 'is-active' : ''} ${maximized ? 'is-maximized' : ''} ${dragging ? 'is-dragging' : ''}`}
    style={maximized ? undefined : { left: position.x, top: position.y }} onPointerDown={onFocus} aria-label={`${app.label} application`}>
    <header className="titlebar" onPointerDown={beginDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={endDrag} onDoubleClick={onMaximize}>
      <div className="traffic-lights" aria-label="Window controls">
        <button className="traffic close" onClick={onClose} aria-label={`Close ${app.label}`}><X /></button>
        <button className="traffic minimize" onClick={onMinimize} aria-label={`Minimize ${app.label}`}><Minimize2 /></button>
        <button className="traffic maximize" onClick={onMaximize} aria-label={`${maximized ? 'Restore' : 'Maximize'} ${app.label}`}><Maximize2 /></button>
      </div>
      <div className="window-title">{app.icon}<span>{app.label}</span></div>
      <div className="titlebar-spacer" />
    </header>
    <div className="window-body">{children}</div>
  </section>;
}

function About() {
  return <div className="about-app">
    <div className="about-hero">
      <div className="portrait"><img src="/photos/jesse-profile.png" alt="Jesse smiling at a restaurant" /></div>
      <div><span className="eyebrow"><i className="status-dot" /> {profile.availability}</span><h1>I’m {profile.name}.<br /><em>{profile.role}.</em></h1><p>{profile.intro}</p><div className="hero-actions"><button onClick={() => document.querySelector<HTMLButtonElement>('[aria-label="Open Projects"]')?.click()}>See my projects <ArrowUpRight /></button><a href={profile.github} target="_blank" rel="noreferrer">GitHub <Code2 /></a></div></div>
    </div>
    <div className="about-grid"><h2>A little about me</h2><div>{profile.bio.map(text => <p key={text}>{text}</p>)}</div></div>
  </div>;
}

const galleryPhotos = [
  { src: '/photos/jesse-profile.png', alt: 'Jesse smiling at a restaurant', caption: 'Good food, good mood' },
  { src: '/photos/jesse-city.png', alt: 'Jesse waiting by a city street', caption: 'Somewhere in the city' },
  { src: '/photos/jesse-friends.png', alt: 'Jesse walking through the city with friends', caption: 'Out with friends' },
  { src: '/photos/jesse-concert.png', alt: 'Jesse and friends at a concert', caption: 'Live music' },
  { src: '/photos/jesse-work.png', alt: 'Jesse wearing a headset', caption: 'Work mode' },
  { src: '/photos/jesse-tram.png', alt: 'Jesse sitting in public transport', caption: 'On the move' }
];

function Gallery() {
  const [selected, setSelected] = useState<number | null>(null);
  const photo = selected === null ? null : galleryPhotos[selected];
  const move = (direction: number) => setSelected(current => current === null ? 0 : (current + direction + galleryPhotos.length) % galleryPhotos.length);
  return <div className="gallery-app">
    <header className="gallery-heading"><div><span className="eyebrow">A little more personal</span><h2>Life beyond<br /><em>the code.</em></h2></div><p>A few unpolished moments with friends, music, city walks, and everyday life.</p></header>
    <div className="photo-grid">{galleryPhotos.map((item, index) => <button key={item.src} className={`photo-tile photo-${index + 1}`} onClick={() => setSelected(index)}>
      <img src={item.src} alt={item.alt} loading="lazy" /><span>{item.caption}</span>
    </button>)}</div>
    {photo && <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photo viewer" onClick={() => setSelected(null)}>
      <button className="lightbox-close" onClick={() => setSelected(null)} aria-label="Close photo"><X /></button>
      <button className="lightbox-nav previous" onClick={event => { event.stopPropagation(); move(-1); }} aria-label="Previous photo"><ChevronLeft /></button>
      <figure onClick={event => event.stopPropagation()}><img src={photo.src} alt={photo.alt} /><figcaption>{photo.caption}</figcaption></figure>
      <button className="lightbox-nav next" onClick={event => { event.stopPropagation(); move(1); }} aria-label="Next photo"><ChevronRight /></button>
    </div>}
  </div>;
}

function Skills() {
  const icons: Record<string, string> = {
    Angular: 'angularjs/angularjs-original.svg', React: 'react/react-original.svg', TypeScript: 'typescript/typescript-original.svg', JavaScript: 'javascript/javascript-original.svg', HTML: 'html5/html5-original.svg', CSS: 'css3/css3-original.svg',
    Flutter: 'flutter/flutter-original.svg', Dart: 'dart/dart-original.svg', Java: 'java/java-original.svg', Python: 'python/python-original.svg', 'C#': 'csharp/csharp-original.svg', '.NET': 'dotnetcore/dotnetcore-original.svg', PHP: 'php/php-original.svg',
    'Node.js': 'nodejs/nodejs-original.svg', Express: 'express/express-original.svg', npm: 'npm/npm-original-wordmark.svg', MySQL: 'mysql/mysql-original.svg', PostgreSQL: 'postgresql/postgresql-original.svg', Docker: 'docker/docker-original.svg', Postman: 'postman/postman-original.svg',
    Git: 'git/git-original.svg', GitHub: 'github/github-original.svg', Bitbucket: 'bitbucket/bitbucket-original.svg', Atlassian: 'jira/jira-original.svg', Figma: 'figma/figma-original.svg'
  };
  const allSkills = skills.flatMap(group => group.items);
  return <div className="skills-app">
    <div className="skills-heading"><div><span className="eyebrow">My technical toolkit</span><h2>Skills & <em>tools.</em></h2></div></div>
    <p className="skills-compact-intro">The technologies I use across web, mobile, backend, databases, collaboration, and design.</p>
    <div className="skills-icon-grid">{allSkills.map(item => <div className="skill-icon-card" key={item}>
      <img src={`https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/${icons[item]}`} alt="" loading="lazy" /><strong>{item}</strong>
    </div>)}</div>
  </div>;
}

function Projects() {
  const [filter, setFilter] = useState('All');
  const visible = filter === 'All' ? projects : projects.filter(project => project.category === filter);
  return <div className="projects-app">
    <div className="project-heading"><div><span className="eyebrow">From my GitHub</span><h2>Projects &<br /><em>contributions.</em></h2></div><p>Real products, experiments, tools, and open-source contributions across web, mobile, APIs, music, and gaming. Open any repository to explore the code.</p></div>
    <div className="filter-row" aria-label="Project filters">{['All', 'Web', 'Product', 'Creative'].map(item => <button className={filter === item ? 'active' : ''} onClick={() => setFilter(item)} key={item}>{item}</button>)}</div>
    <div className="project-grid">{visible.map(project => <article className="project-card" key={project.title}>
      <div className={`project-visual image-${project.imageFit ?? 'cover'}`} style={{ '--accent': project.accent } as React.CSSProperties}><img src={project.image} alt={`${project.title} project preview`} loading="lazy" /><span>{project.number}</span><ArrowUpRight /></div>
      <div className="project-meta"><span>{project.category} · {project.year}</span></div><h3>{project.title}</h3><p>{project.description}</p>
      <ul>{project.technologies.map(tech => <li key={tech}>{tech}</li>)}</ul>
      <div className="project-links">{project.live && <a href={project.live} target="_blank" rel="noreferrer" aria-label={`View ${project.title} live`}>Live site <ExternalLink /></a>}{project.source && <a href={project.source} target="_blank" rel="noreferrer" aria-label={`View ${project.title} source code`}>GitHub <Code2 /></a>}{project.privateRepository && <span>Private team repository <Code2 /></span>}</div>
    </article>)}</div>
  </div>;
}

function Experience() {
  return <div className="experience-app">
    <div className="section-intro"><span className="eyebrow">The path so far</span><h2>Experience &<br /><em>education.</em></h2><p>Building a strong software-development foundation through education, certification, real projects, and experience in a professional development team.</p></div>
    <div className="timeline">{timeline.map((item, index) => <article key={item.period + item.title}>
      <div className="timeline-marker">{item.type === 'Work' ? <BriefcaseBusiness /> : <GraduationCap />}</div>
      <div className="timeline-date"><span>{item.type}</span><strong>{item.period}</strong></div>
      <div className="timeline-copy"><span>0{index + 1}</span><h3>{item.title}</h3><h4>{item.place}</h4><p>{item.detail}</p></div>
    </article>)}</div>
  </div>;
}

function Contact() {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setState('sending');
    const form = event.currentTarget;
    const body = Object.fromEntries(new FormData(form).entries());
    try {
      const response = await fetch(apiUrl('contact'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!response.ok) throw new Error();
      setState('sent'); form.reset();
    } catch { setState('error'); }
  }
  return <div className="contact-app">
    <div className="contact-copy"><span className="eyebrow">Start a conversation</span><h2>Have a good idea?<br /><em>Let’s talk.</em></h2><p>Tell me what you’re working on, where you’re stuck, or just say hello. I usually reply within two working days.</p>
      <a href={`mailto:${profile.email}`}><Mail /> <span><small>Email me directly</small>{profile.email}</span></a>
      <div className="availability-card"><i className="status-dot" /><span><strong>Available for selected projects</strong><small>Remote · Europe / worldwide</small></span></div>
    </div>
    <form onSubmit={submit} className="contact-form">
      <label><span>Your name</span><input name="name" type="text" autoComplete="name" placeholder="Ada Lovelace" required minLength={2} maxLength={80} /></label>
      <label><span>Email address</span><input name="email" type="email" autoComplete="email" placeholder="ada@example.com" required maxLength={120} /></label>
      <label><span>What’s this about?</span><select name="subject" required defaultValue=""><option value="" disabled>Choose a topic</option><option>Project enquiry</option><option>Job opportunity</option><option>Collaboration</option><option>Just saying hello</option></select></label>
      <label><span>Your message</span><textarea name="message" placeholder="A little about your idea, timing, and what you need…" required minLength={20} maxLength={3000} rows={6} /></label>
      <input name="website" className="honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <button className="send-button" type="submit" disabled={state === 'sending' || state === 'sent'}>{state === 'sending' ? 'Sending…' : state === 'sent' ? <>Message sent <Check /></> : <>Send message <Send /></>}</button>
      <p className={`form-status ${state}`} role="status">{state === 'sent' ? 'Thanks — your message is safely on its way.' : state === 'error' ? `Something went wrong. Please email ${profile.email} instead.` : 'No spam. Your details are only used to reply.'}</p>
    </form>
  </div>;
}

type Track = { playing: boolean; title: string; artist: string; album: string; art: string | null; progress: number; duration: number; url: string };
type MusicPalette = { primary: string; secondary: string; surface: string };
const demoTrack: Track = { playing: false, title: 'Nothing is playing', artist: 'Connect Spotify to see live listening', album: 'Music app', art: null, progress: 0, duration: 1, url: 'https://open.spotify.com' };
const defaultMusicPalette: MusicPalette = { primary: 'rgb(77 235 132)', secondary: 'rgb(189 255 60)', surface: 'rgb(13 35 23)' };
function formatTime(ms: number) { const sec = Math.floor(ms / 1000); return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`; }

function extractAlbumPalette(image: HTMLImageElement): MusicPalette {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 48; canvas.height = 48;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return defaultMusicPalette;
    context.drawImage(image, 0, 0, 48, 48);
    const pixels = context.getImageData(0, 0, 48, 48).data;
    const buckets = new Map<string, { r: number; g: number; b: number; count: number; score: number }>();
    for (let index = 0; index < pixels.length; index += 16) {
      const r = pixels[index]; const g = pixels[index + 1]; const b = pixels[index + 2]; const alpha = pixels[index + 3];
      if (alpha < 180) continue;
      const maximum = Math.max(r, g, b); const minimum = Math.min(r, g, b);
      const saturation = maximum - minimum;
      if (maximum < 28 || minimum > 238) continue;
      const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
      const bucket = buckets.get(key) ?? { r: 0, g: 0, b: 0, count: 0, score: 0 };
      bucket.r += r; bucket.g += g; bucket.b += b; bucket.count += 1; bucket.score += 1 + saturation / 90;
      buckets.set(key, bucket);
    }
    const colors = [...buckets.values()].sort((a, b) => b.score - a.score).map(bucket => ({
      r: Math.round(bucket.r / bucket.count), g: Math.round(bucket.g / bucket.count), b: Math.round(bucket.b / bucket.count)
    }));
    if (!colors.length) return defaultMusicPalette;
    const dominant = colors[0];
    const vibrancy = (color: { r: number; g: number; b: number }) => {
      const chroma = Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);
      const brightness = (color.r + color.g + color.b) / 3;
      return chroma * (.4 + brightness / 255);
    };
    const primary = [...colors.slice(0, 12)].sort((a, b) => vibrancy(b) - vibrancy(a))[0] ?? dominant;
    const secondary = Math.hypot(dominant.r - primary.r, dominant.g - primary.g, dominant.b - primary.b) > 50
      ? dominant
      : colors.find(color => Math.hypot(color.r - primary.r, color.g - primary.g, color.b - primary.b) > 78) ?? primary;
    const lift = (color: { r: number; g: number; b: number }, amount: number) => ({
      r: Math.round(color.r + (255 - color.r) * amount),
      g: Math.round(color.g + (255 - color.g) * amount),
      b: Math.round(color.b + (255 - color.b) * amount)
    });
    const luminance = (primary.r * .299 + primary.g * .587 + primary.b * .114);
    const accent = luminance < 88 ? lift(primary, .38) : primary;
    const surface = { r: Math.round(dominant.r * .18), g: Math.round(dominant.g * .18), b: Math.round(dominant.b * .18) };
    const css = (color: { r: number; g: number; b: number }) => `rgb(${color.r} ${color.g} ${color.b})`;
    return { primary: css(accent), secondary: css(secondary), surface: css(surface) };
  } catch { return defaultMusicPalette; }
}

function Music() {
  const [track, setTrack] = useState<Track>(demoTrack);
  const [palette, setPalette] = useState<MusicPalette>(defaultMusicPalette);
  const [titleOverflow, setTitleOverflow] = useState(0);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    async function refreshTrack() {
      try {
        const response = await fetch(apiUrl('spotify/now-playing'), { signal: controller.signal, cache: 'no-store' });
        if (response.status === 204) return setTrack(demoTrack);
        if (!response.ok) return;
        setTrack(await response.json() as Track);
      } catch { /* Keep the last valid track when the connection is interrupted. */ }
    }
    void refreshTrack();
    const poll = window.setInterval(refreshTrack, 30_000);
    return () => { controller.abort(); window.clearInterval(poll); };
  }, []);
  useEffect(() => {
    if (!track.playing) return;
    const timer = window.setInterval(() => {
      setTrack(current => current.playing
        ? { ...current, progress: Math.min(current.duration, current.progress + 1000) }
        : current);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [track.playing, track.title, track.duration]);
  useEffect(() => {
    const heading = titleRef.current;
    const title = heading?.firstElementChild as HTMLElement | null;
    if (!heading || !title) return;
    const measure = () => setTitleOverflow(Math.max(0, Math.ceil(title.scrollWidth - heading.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(heading);
    observer.observe(title);
    return () => observer.disconnect();
  }, [track.title]);
  return <div className="music-app" style={{ '--music-primary': palette.primary, '--music-secondary': palette.secondary, '--music-surface': palette.surface } as React.CSSProperties}>
    <header className="music-header"><div className="music-logo"><Music2 /><strong>Music</strong><span>Now playing</span></div><div className="spotify-badge"><i /><span>Live from Spotify</span></div></header>
    <div className="music-main">
      <section id="now" className="now-playing">
        <div className={`album-art ${track.playing ? 'is-playing' : ''}`}>{track.art ? <img src={track.art} crossOrigin="anonymous" alt={`${track.album} album artwork`} onLoad={event => setPalette(extractAlbumPalette(event.currentTarget))} /> : <><Music2 /><i /><i /></>}</div>
        <div className={`track-copy ${track.title === demoTrack.title ? 'is-placeholder' : ''}`}><span className="live-label"><i /> {track.playing ? 'Playing now' : 'Spotify connected'}</span><h2 ref={titleRef} title={track.title} className={titleOverflow > 0 ? 'is-overflowing' : ''}><span style={{ '--title-travel': `${titleOverflow}px`, '--title-duration': `${Math.min(16, Math.max(7, titleOverflow / 28 + 6))}s` } as React.CSSProperties}>{track.title}</span></h2><p>{track.artist}</p><small>{track.album}</small>
          <div className="progress"><div><span>{formatTime(track.progress)}</span><span>{formatTime(track.duration)}</span></div><div className="progress-track"><i style={{ width: `${Math.min(100, track.progress / track.duration * 100)}%` }} /></div></div>
          <div className="player-controls"><button aria-label="Previous track"><SkipBack /></button><button className="play" aria-label={track.playing ? 'Pause' : 'Play'}>{track.playing ? <Pause /> : <Play />}</button><button aria-label="Next track"><SkipForward /></button><Volume2 /></div>
          <a href={track.url} target="_blank" rel="noreferrer">Open in Spotify <ArrowUpRight /></a>
        </div>
      </section>
    </div>
  </div>;
}

function Terminal({ openApp }: { openApp: (id: AppId) => void }) {
  const [history, setHistory] = useState<string[]>(['Last login: today on portfolio', 'Type “help” to see available commands.']);
  const [command, setCommand] = useState('');
  function run(event: FormEvent) {
    event.preventDefault(); const value = command.trim().toLowerCase(); setCommand('');
    if (!value) return;
    if (value === 'clear') return setHistory([]);
    const target = apps.find(app => app.id === value);
    let response = `zsh: command not found: ${value}`;
    if (target) { openApp(target.id); response = `Opening ${target.label}…`; }
    if (value === 'help') response = `Commands: ${apps.map(app => app.id).filter(id => id !== 'terminal').join(', ')}, whoami, clear`;
    if (value === 'whoami') response = `${profile.name} — ${profile.role} based in ${profile.location}.`;
    setHistory(items => [...items, `visitor@portfolio ~ % ${value}`, response]);
  }
  return <div className="terminal-app" onClick={() => document.getElementById('terminal-input')?.focus()}>
    <div className="terminal-history">{history.map((line, index) => <p key={index} className={line.startsWith('visitor') ? 'command' : ''}>{line}</p>)}</div>
    <form onSubmit={run}><label htmlFor="terminal-input">visitor@portfolio ~ %</label><input id="terminal-input" value={command} onChange={e => setCommand(e.target.value)} autoComplete="off" spellCheck={false} aria-label="Terminal command" /></form>
    <div className="terminal-shortcuts">{apps.filter(app => !['terminal'].includes(app.id)).map(app => <button key={app.id} onClick={() => openApp(app.id)}>{app.id}</button>)}</div>
  </div>;
}

const content: Partial<Record<AppId, (open: (id: AppId) => void) => ReactNode>> = {
  about: () => <About />, gallery: () => <Gallery />, skills: () => <Skills />, projects: () => <Projects />, experience: () => <Experience />,
  contact: () => <Contact />, music: () => <Music />, terminal: open => <Terminal openApp={open} />
};

function App() {
  const [openApps, setOpenApps] = useState<AppId[]>(['about']);
  const [active, setActive] = useState<AppId>('about');
  const [minimized, setMinimized] = useState<AppId[]>([]);
  const [maximized, setMaximized] = useState<AppId[]>([]);
  const [positions, setPositions] = useState(initialPositions);
  const [time, setTime] = useState(new Date());
  const [launcherOpen, setLauncherOpen] = useState(false);

  useEffect(() => { const timer = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(timer); }, []);
  const currentApp = apps.find(app => app.id === active)?.label ?? 'Finder';
  const dateTime = useMemo(() => time.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }), [time]);
  function openApp(id: AppId) {
    setOpenApps(items => items.includes(id) ? items : [...items, id]);
    setMinimized(items => items.filter(item => item !== id)); setActive(id); setLauncherOpen(false);
  }
  function closeApp(id: AppId) {
    const remaining = openApps.filter(item => item !== id); setOpenApps(remaining); setMinimized(items => items.filter(item => item !== id));
    if (active === id) setActive(remaining.at(-1) ?? 'finder');
  }
  return <div className="desktop-shell">
    <header className="menubar">
      <div className="menu-left"><button className="brand-mark" onClick={() => setLauncherOpen(value => !value)} aria-label="Open application menu">J</button><strong>{currentApp}</strong><nav aria-label="Application menu"><button>File</button><button>View</button><button onClick={() => openApp('contact')}>Let’s talk</button></nav></div>
      <div className="menu-right"><Wifi /><Search /><span>{dateTime}</span></div>
    </header>
    {launcherOpen && <div className="launcher" role="dialog" aria-label="Applications"><div className="launcher-top"><strong>Applications</strong><button onClick={() => setLauncherOpen(false)} aria-label="Close applications"><X /></button></div><div>{apps.map(app => <AppIcon app={app} onOpen={() => openApp(app.id)} key={app.id} />)}</div></div>}
    <main id="desktop" className="desktop" onPointerDown={() => setLauncherOpen(false)}>
      <div className="desktop-copy"><img src="/photos/jesse-profile.png" alt="" /><div><span>Hello, I’m</span><h1>Jesse<br /><em>van de Velde.</em></h1><p>{profile.role} · {profile.location}</p></div></div>
      <div className="desktop-icons">{desktopAppIds.map(id => {
        const app = apps.find(item => item.id === id)!;
        return <AppIcon app={app} onOpen={() => openApp(app.id)} key={app.id} />;
      })}</div>
      {openApps.map((id, index) => {
        const app = apps.find(item => item.id === id)!;
        return <div key={id} style={{ zIndex: active === id ? 100 : 10 + index }} className="window-layer">
          <Window app={app} active={active === id} minimized={minimized.includes(id)} maximized={maximized.includes(id)} position={positions[id]}
            onFocus={() => setActive(id)} onClose={() => closeApp(id)} onMinimize={() => setMinimized(items => [...items, id])}
            onMaximize={() => setMaximized(items => items.includes(id) ? items.filter(item => item !== id) : [...items, id])}
            onMove={position => setPositions(items => ({ ...items, [id]: position }))}>{content[id]?.(openApp)}</Window>
        </div>;
      })}
    </main>
    <nav className="dock" aria-label="Applications dock">
      {dockAppIds.map(id => {
        const app = apps.find(item => item.id === id)!;
        return <div className="dock-item" key={app.id}><AppIcon compact app={app} onOpen={() => openApp(app.id)} />{openApps.includes(app.id) && <i className="running-dot" />}</div>;
      })}
    </nav>
    <div className="mobile-home-indicator" />
  </div>;
}

export default App;
